<template>
  <div>
    <div>
      <ontario-input 
        :input-object="nameInput"
        v-model="nameInput.value"
      />
    </div>

    <h5>{{locale("Intro.Dropdowns.Date")}}</h5>
    <div class="intro-inputs">
      <ontario-dropdown
        :dropdown-object="yearDropdown"
        v-model="yearDropdown.value"
        v-on:input="onUpdateYear"
      />
      <ontario-dropdown
        :dropdown-object="monthDropdown"
        v-model="monthDropdown.value"
        v-on:input="onUpdateMonth"
      />
      <ontario-dropdown
        :dropdown-object="dayDropdown"
        v-model="dayDropdown.value"
        v-on:input="onUpdateDay"
      />
      <ontario-alert
        class="error-margin"
        type="error" 
        :title="locale('error')" 
        :show-icon="false"
        v-show="errorMessage == true"
      >
        {{ this.locale('invalidDOB') }}
      </ontario-alert>
    </div>

    <ontario-button
      id="getSchedule"
      btn-type="primary"
      :label="locale('Intro.Button')"
      v-on:click="getScheduleButton"
    />
  </div>
</template>

<script>
import drupal from '../../../shared/mixins/drupal';
import Dropdown from '../../../shared/components/Dropdown';
import MultiOptionField from '../../../shared/functions/MultiOptionField';
import InputOption from '../../../shared/functions/InputOption';
import Input from '../../../shared/components/Input';
import InputField from '../../../shared/functions/InputField';
import Button from '../../../shared/components/Button';
import Alert from '../../../shared/components/Alert';
import { getBirthdayYears, getBirthdayDates, convertToMomentObject } from '../functions/birthdayDropdown';

// Get the dropdown values for the child's birthday using getBirthdayYears.
// getBirthdayDates: [years, months, [[days]]]
const dateDropdownOptions = getBirthdayDates(getBirthdayYears());

export default {
  name: 'IntroInputs',
  mixins: [drupal],
  props: {
    entityType: String
  },
  components: {
    OntarioDropdown: Dropdown,
    OntarioInput: Input,
    OntarioButton: Button,
    OntarioAlert: Alert
  },
  data() {
    return {
      errorMessage: false,
      nameInput:  
        InputField(
          'childName',
          '',
          'String',
          false,
          this.locale('Intro.Dropdowns.Name'),
          ""
        ),
      dateChosen: '',
      yearChosen: 0,
      monthChosen: 1,
      dayChosen: 1,
      // Map through years and add as options.
      yearDropdown: MultiOptionField(
        'birthdayYearDropdown',
        this.locale('Intro.Dropdowns.Year'),
        dateDropdownOptions[0].map((year, index) => InputOption(index.toString(), index + 1, year)),
        true,
        0
      ),
      // Map through months and add as options.
      monthDropdown: MultiOptionField(
        'birthdayMonthDropdown',
        this.locale('Intro.Dropdowns.Month'),
        dateDropdownOptions[1][0].map((month, index) => InputOption(index.toString(), index + 1, this.moment().month(month - 1).locale(this.$i18n.locale).format('MMMM'))),
        true,
        'Select'
      ),
      // Map through days and add as options.
      dayDropdown: MultiOptionField(
        'birthdayDayDropdown',
        this.locale('Intro.Dropdowns.Day'),
        dateDropdownOptions[2][0][0].map((day, index) => InputOption(index.toString(), index + 1, day)),
        true,
        0
      ),
    }
  },
  methods: {
    // Same methods as in Holiday Pay app with slight adjustments to make the datepicker end at present date.
    // When the year updates, the day dropdown could possibly be different (Ex. 2020 is a leap year).
    onUpdateYear(selectedYear) {
      this.errorMessage = false;
      this.yearChosen = selectedYear - 1;
      this.monthDropdown.options = dateDropdownOptions[1][this.yearChosen].map((month, index) => InputOption(index.toString(), index, this.moment().month(month - 1).locale(this.$i18n.locale).format('MMMM')));

      if (dateDropdownOptions[2][this.yearChosen][this.monthChosen][this.dayChosen]) {
        this.dayDropdown.options = dateDropdownOptions[2][this.yearChosen][this.monthChosen].map((day, index) => InputOption(index.toString(), index + 1, day));
        this.dateChosen = convertToMomentObject(dateDropdownOptions[0][this.yearChosen], dateDropdownOptions[1][this.yearChosen][this.monthChosen], this.dayChosen);
      } else {
        this.dateChosen = 'Invalid date';
      }

      if (this.dateChosen == 'Invalid date') {
        this.dayDropdown.value = 0;
        this.dayChosen = 1;
        this.monthDropdown.value = 'Select';
        this.monthChosen = 1;
        this.dateChosen = convertToMomentObject(dateDropdownOptions[0][this.yearChosen], dateDropdownOptions[1][this.yearChosen][this.monthChosen], this.dayChosen);
      }
    },
    // When the month updates, the number of days in that month changes therefore needing to update the day options.
    onUpdateMonth(selectedMonth) {
      this.errorMessage = false;
      this.monthChosen = selectedMonth;
      this.dayDropdown.options = dateDropdownOptions[2][this.yearChosen][this.monthChosen].map((day, index) => InputOption(index.toString(), index + 1, day));
      this.dateChosen = convertToMomentObject(dateDropdownOptions[0][this.yearChosen], dateDropdownOptions[1][this.yearChosen][this.monthChosen], this.dayChosen);

      if (this.dateChosen == 'Invalid date') {
        this.dayDropdown.value = 1;
        this.dayChosen = 1;
        this.dateChosen = convertToMomentObject(dateDropdownOptions[0][this.yearChosen], dateDropdownOptions[1][this.yearChosen][this.monthChosen], this.dayChosen);
      }
    },
    onUpdateDay(selectedDay) {
      this.errorMessage = false;
      this.dayChosen = selectedDay;
      this.dateChosen = convertToMomentObject(dateDropdownOptions[0][this.yearChosen], dateDropdownOptions[1][this.yearChosen][this.monthChosen], this.dayChosen);
    },
    getScheduleButton() {
      this.submitted = true;
      // If date selection isn't filled out, throw an error.
      if ([this.dayDropdown.value, this.yearDropdown.value].includes(0) || [this.dayDropdown.value, this.monthDropdown.value, this.yearDropdown.value].includes('Select')) {
        this.errorMessage = true;
      } else {
        this.$emit('submit', [this.nameInput.value, this.dateChosen]);
      }
    },
    resetState() {
      this.nameInput.value = '';
      this.yearDropdown.value = 'Select';
      this.monthDropdown.value = 'Select';
      this.dayDropdown.value = 'Select';
    }
  }
}

</script>

<style lang="scss"> 
  .intro-inputs {
    display: flex;
    flex-wrap: wrap;
    max-width: 736px;
  }
  .intro-inputs > div {
    flex: 1 0 30.5%;
    margin: 5px;
  }
  #childName {
    max-width: 736px;
  }
  @media only screen and (max-width: 640px) {
    .intro-inputs > div {
      flex-basis: 50%;
    }
  }
  .error-margin {
    margin-bottom: 2em !important;
  }
</style>